import { augmentIncomingCreativeMedia } from 'components/Recommendations/Campaign/Media/mediaPayload'
import { TARGETING_MODES } from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/targeting'
import { isObjectEmpty } from 'utils/helpers'

import { parseOrRaw } from 'domain/shared/validation'
import { campaignItemSchema, campaignsResponseSchema } from './schemas'

// v4 marks `latest_campaign_setup` "Obsolete: For backwards compatibility"
// and its `.targeting` is now the platform-nested {fb, google} shape (or
// absent entirely for a campaign with no CampaignSetup row yet) — not the
// v3 flat {targeting_mode, 'postal-codes', 'range-locations'} shape this
// parses. Only take the legacy branch when the v3 shape is actually
// present; otherwise synthesize a single default fd_targeting entry, same
// as v3 already did for "published without targeting".
function mapLatestCampaignSetupTargeting(latestCampaignSetup, campaign) {
  const incomingTargeting = latestCampaignSetup.targeting

  if (!incomingTargeting?.targeting_mode) {
    return [
      {
        tc: campaign.tc,
        tc_run_id: Number(campaign.tc_run_id),
        targetingMode: TARGETING_MODES.fdTargeting,
      },
    ]
  }

  let targetingMode = incomingTargeting.targeting_mode

  if (!targetingMode) {
    targetingMode = !isObjectEmpty(incomingTargeting['postal-codes']?.countries)
      ? TARGETING_MODES.postalCodes
      : !isObjectEmpty(incomingTargeting['range-locations']?.included) ||
          !isObjectEmpty(incomingTargeting['range-locations']?.excluded)
        ? TARGETING_MODES.rangeLocations
        : TARGETING_MODES.fdTargeting
  }

  return [
    {
      ...incomingTargeting,
      targetingMode,
      postalCodes: incomingTargeting['postal-codes'],
      rangeLocations: incomingTargeting['range-locations'],
    },
  ]
}

function mapLatestCampaignSetup(campaign) {
  const latestCampaignSetup = campaign.latest_campaign_setup ?? {}
  const creative = latestCampaignSetup.creative ?? {}
  const mergedMedia = augmentIncomingCreativeMedia({
    media: creative.media ?? [],
    placement_media: creative.placement_media ?? [],
  })

  return {
    ...latestCampaignSetup,
    creative: {
      ...creative,
      media: mergedMedia,
      placement_media: [],
      excluded_variations: creative.excluded_variations ?? [],
    },
    targeting: mapLatestCampaignSetupTargeting(latestCampaignSetup, campaign),
  }
}

// v4 response -> FE domain model, replacing v3's formatApiCampaign. Every
// top-level field other than latest_campaign_setup is unchanged between v3
// and v4 (confirmed field-by-field against the backend serializers) — this
// mapper's output shape intentionally matches formatApiCampaign's exactly,
// so existing consumers (RunningCampaigns.jsx, OpenCampaigns.jsx,
// CampaignHeader, etc.) don't need to change.
export function mapCampaignItem(campaign) {
  const source = parseOrRaw(campaignItemSchema, campaign)

  return {
    alias: source.alias,
    id: source.id,
    cid: `${source.tc_run_id}_${source.eid}_${source.tc.replace(/\s+/g, '-')}`,
    tc_run_id: Number(source.tc_run_id),
    eid: source.eid,
    channel: source.channel,
    cr_start_days_upfront: source.cr_start_days_upfront,
    cr_end_days_upfront: source.cr_end_days_upfront,
    event: source.event ? { start_date_time: source.event.start_date_time } : {},
    run_time: source.run_time,
    tc_title: source.tc,
    tc: source.tc,
    tc_size: source.tc_size,
    ad_budget: source.budget,
    currency: source.currency,
    buying_power: source.buying_power,
    attractiveness: source.attractiveness,
    potential: source.potential,
    rank: source.rank,
    // set_up_date has never existed on either the v3 or v4 Campaign
    // model/serializer — this branch is always false; kept only to match
    // formatApiCampaign's prior (already-dead) behavior exactly.
    set_up_date: source.set_up_date,
    status: source.set_up_date ? 'running' : source.status,
    credits: source.credits,
    fb_adset_end_time: source.fb_adset_end_time,
    published: source.published,
    initial_set_up_date: source.initial_set_up_date,
    creative: source.creative
      ? {
        body: source.creative.body,
        headline: source.creative.headline,
        media: augmentIncomingCreativeMedia({
          media: source.creative.media ?? [],
          placement_media: source.creative.placement_media ?? [],
        }),
        placement_media: [],
        destination_url: source.creative.destination_url,
        description: source.creative.description,
        integrations: source.creative.integrations ?? [],
      }
      : {},
    creativeSuggestion: source.suggested_creative
      ? {
        body: source.suggested_creative.body ?? [],
        headline: source.suggested_creative.headline ?? [],
        media: (source.suggested_creative.media ?? []).map((media) => media.description),
        description: source.suggested_creative.description,
      }
      : {},
    latest_campaign_setup: mapLatestCampaignSetup(source),
  }
}

export function mapCampaignsResponse(data) {
  const source = parseOrRaw(campaignsResponseSchema, data)

  return {
    total: source.total,
    items: (source.items ?? []).map(mapCampaignItem),
  }
}
